import React, { useEffect, useMemo, useState } from 'react';
import { __ } from '../../translations';
import { pluck } from '../../helpers/common';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { log } from '@intigral/utils/lib/logger';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import { useContinueWatchingFilters } from '../../hooks/useContinueWatching';
import type { ContentItem } from '../../hooks/useContinueWatching';
import ContinueWatchCarousel from './ContinueWatchCarousel';
import { useGetBookMarksQuery } from '@intigral/domain/lib/presentation/hooks/useGetBookMarksQuery';
import user from '../../helpers/user';
import h, { getCountry } from '../../helpers/common';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { useSelector } from 'react-redux';
import { IStore } from '../../interfaces/StoreTypes';
import { transformLinearResponse } from '../../utils/transformResponse/thinkAnalyticsResponse';
import { isLinearContentItem, isLinearTaCarousel, normalizeBookmarkList } from '../../hooks/data/taContinueWatchUtils';

interface VODDetail {
    guid: string;
    runtime?: number;
    year?: string;
    ratings?: Array<{ rating: string }>;
    tags?: Array<{ scheme: string; title: string }>;
}

interface Languages {
    ar?: string;
    en?: string;
}

interface TAContinueWatchProps {
    content: ContentItem[];
    rawEntries?: Record<string, unknown>[];
    id?: string;
    meta?: {
        title?: Languages;
    };
    name?: string;
    carousel?: Carousel;
}

type BookmarkEntry = {
    paId?: string;
    id?: string;
    guid?: string;
    timeCode?: number | string;
    timecode?: number | string;
    listings?: Array<{
        startTime?: string;
        endTime?: string;
        id?: string | number;
        stationId?: string;
    }>;
};

type LinearContentItem = ContentItem & {
    startTime?: string | number;
    endTime?: string | number;
    stationId?: string;
    listingId?: string;
    textAr?: string;
    textAR?: string;
    title?: string;
};

const _getDataByGuid = (durations: VODDetail[], guid: string): VODDetail | undefined =>
    Array.isArray(durations) && durations.find((el) => el.guid === guid);

const _findBookmark = (bookmarks: BookmarkEntry[], item: ContentItem): BookmarkEntry | undefined =>
    bookmarks.find((bookmark) => bookmark.paId === item.Id || bookmark.id === item.Id || bookmark.guid === item.guid);

const _constructVodData = (item: ContentItem, vodDetails: VODDetail[], bookmarks: BookmarkEntry[]) => {
    const vodData = _getDataByGuid(vodDetails, item.guid);
    let duration = 0;
    let timeCode = 0;
    let isBookmarked = false;
    if (item?.programType === 'series') {
        const episode = bookmarks.find((bookmark) => bookmark.paId === item?.episodeDetails?.episodeId);
        timeCode = Number(episode?.timecode ?? episode?.timeCode ?? 0);
        duration = (item?.episodeDetails && item?.episodeDetails?.duration) || 0;
        isBookmarked = !!episode;
    } else {
        const movie = bookmarks.find((bookmark) => bookmark.paId === item?.Id);
        timeCode = Number(movie?.timecode ?? movie?.timeCode ?? 0);
        duration = item?.duration || 0;
        isBookmarked = !!movie;
    }
    const percentageNumber = duration > 0 ? ((timeCode || 0) / duration) * 100 : 0;
    const tags = vodData?.tags;
    if (tags) {
        tags.forEach((el) => {
            if (el.scheme === 'Genre') {
                __(el.title);
            }
        });
    }
    return { percentageNumber, isBookmarked };
};

const _getLinearDuration = (startTime?: string | number, endTime?: string | number): number => {
    if (startTime == null || endTime == null) {
        return 0;
    }
    return Math.max(
        0,
        Math.round(new Date(endTime).getTime() / 1000) - Math.round(new Date(startTime).getTime() / 1000)
    );
};

const _constructLinearData = (item: LinearContentItem, bookmarks: BookmarkEntry[]) => {
    const bookmark = _findBookmark(bookmarks, item);
    const listing = bookmark?.listings?.[0];
    const startTime = item.startTime ?? listing?.startTime;
    const endTime = item.endTime ?? listing?.endTime;
    const stationId = item.stationId ?? listing?.stationId;
    const listingId = item.listingId ?? (listing?.id != null ? String(listing.id) : undefined);
    const timeCode = Number(bookmark?.timeCode ?? bookmark?.timecode ?? item.timeCode ?? 0);
    const duration = _getLinearDuration(startTime, endTime) || item.duration || 0;
    const percentageNumber = duration > 0 ? (timeCode / duration) * 100 : 0;
    const textEn = item.textEn ?? item.title ?? item.text;
    const textAr = item.textAr ?? item.textAR ?? item.text;
    const text = item.text ?? item.title ?? textEn;

    return {
        programType: 'LINEAR',
        startTime,
        endTime,
        stationId,
        listingId,
        timeCode,
        text,
        textEn,
        textAr,
        title: textEn,
    };
};

const mapLinearContinueWatchAsset = (item: LinearContentItem, bookmarks: BookmarkEntry[]): ContentItem => ({
    ...item,
    image_landscape: item.image_landscape || item.image,
    ..._constructLinearData(item, bookmarks),
});

export const TAContinueWatch: React.FC<TAContinueWatchProps> = (props) => {
    const continueWatchData = Array.isArray(props?.content) ? props.content : [];
    const rawEntries = props.rawEntries;
    const [vodsDetails, setVodsDetails] = useState<VODDetail[]>([]);
    const { programType } = useContinueWatchingFilters();
    const { config } = useConfigurationSections();
    const { userChannels } = useSelector((state: IStore) => state.liveTv);
    const { data: bookmarks } = useGetBookMarksQuery({
        apiKey: user.getAPIKey(),
        profileGuid: user.getProfile()?.profileGuid,
        authToken: user.getUserToken(),
        identityToken: user.getIdentityToken(),
        country: getCountry(),
        device: h.getDevice(),
        programType: 'all',
    });

    const bookmarkList = useMemo(() => normalizeBookmarkList(bookmarks), [bookmarks]);

    const isLinear = useMemo(
        () => isLinearTaCarousel(props.carousel, rawEntries) || continueWatchData.some(isLinearContentItem),
        [props.carousel, rawEntries, continueWatchData]
    );

    const contentItems = useMemo(() => {
        if (!isLinear) {
            return continueWatchData;
        }

        const entries = rawEntries?.length ? rawEntries : continueWatchData;
        return entries.map((entry) => {
            const linearItem = rawEntries?.length
                ? (transformLinearResponse(
                      entry,
                      config?.promotionProviderImages,
                      userChannels
                  ) as unknown as LinearContentItem)
                : (entry as LinearContentItem);

            return mapLinearContinueWatchAsset(linearItem, bookmarkList);
        });
    }, [isLinear, rawEntries, continueWatchData, config?.promotionProviderImages, userChannels, bookmarkList]);

    useEffect(() => {
        if (isLinear || contentItems.length === 0) {
            setVodsDetails([]);
            return;
        }

        let cancelled = false;

        (async () => {
            const params = {
                fields: 'guid,runtime,year,ratings,tags',
                byguid: pluck(contentItems, 'guid')?.join('|') || '',
                byProgramType: programType,
            };
            const response = await container.resolve<IMpxService>(DI.SERVICES.IMpxService).getFeed('all-vods', params);

            if (!cancelled) {
                setVodsDetails(response?.entries || []);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [contentItems, isLinear, programType]);

    const assets = useMemo(() => {
        if (isLinear) {
            return contentItems;
        }

        if (!vodsDetails.length) {
            return [];
        }

        return continueWatchData
            .filter((item) => !!vodsDetails.find((vod) => vod.guid === item.guid))
            .map((vod: any) => ({
                ...vod,
                image_landscape: vod?.episodeDetails
                    ? vod?.episodeDetails.thumbnails['landscape-highestResolution']?.url
                    : vod?.image_landscape,
                Image: vod?.episodeDetails
                    ? vod?.episodeDetails.thumbnails['ortrait-highestResolution']?.url
                    : vod?.image,
            }))
            .map((asset) => ({
                ...asset,
                ..._constructVodData(asset, vodsDetails, bookmarkList),
            }));
    }, [isLinear, contentItems, continueWatchData, vodsDetails, bookmarkList]);

    const shouldRender = isLinear ? assets.length > 0 : vodsDetails.length > 0 && assets.length > 0;

    if (!shouldRender) {
        return null;
    }

    return (
        <ContinueWatchCarousel
            componentId={props.id || ''}
            carouselId={props?.carousel?.id}
            meta={props.meta}
            assets={assets}
            name={props.name}
        />
    );
};
